// @systeme: interface
// L'onglet Moi : profil, foyer, abonnement, compteurs. Et la porte « Un souci, une idée ? ».
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { ReponseMoi } from '@foyer/shared';
import { Link as LienRouteur } from 'react-router';
import { Chargement } from '../composants/Chargement';
import { FormulaireRetour } from '../composants/FormulaireRetour';
import { SectionAbonnement } from '../composants/SectionAbonnement';
import { SectionCalendrier } from '../composants/SectionCalendrier';
import { SectionCompte } from '../composants/SectionCompte';
import { SectionFoyer } from '../composants/SectionFoyer';
import { SectionNotifications } from '../composants/SectionNotifications';
import { useMessages } from '../composants/Messages';
import { api } from '../lib/api';
import { textes } from '../lib/textes';
import { useSession } from '../session/SessionContext';
import { tailles } from '../theme/theme';

export function Moi() {
  const { deconnecter, mettreAJour } = useSession();
  const { dire, direErreur } = useMessages();
  const [moi, setMoi] = useState<ReponseMoi | undefined>();
  const [prenom, setPrenom] = useState('');
  const [nomEspace, setNomEspace] = useState('');
  const [retourOuvert, setRetourOuvert] = useState(false);

  useEffect(() => {
    api.moi
      .lire()
      .then((r) => {
        setMoi(r);
        setPrenom(r.utilisateur.prenom);
        setNomEspace(r.espace.nom);
      })
      .catch(direErreur);
  }, [direErreur]);

  async function enregistrer() {
    if (!moi) return;
    const changements = {
      ...(prenom.trim() !== moi.utilisateur.prenom ? { prenom: prenom.trim() } : {}),
      ...(nomEspace.trim() !== moi.espace.nom ? { nomEspace: nomEspace.trim() } : {}),
    };
    if (Object.keys(changements).length === 0) return;
    try {
      const r = await api.moi.modifier(changements);
      setMoi(r);
      mettreAJour({
        utilisateur: r.utilisateur,
        espace: r.espace,
        fonctionnalites: r.fonctionnalites,
      });
      dire(textes.cestNote);
    } catch (e) {
      direErreur(e);
    }
  }

  if (!moi) return <Chargement />;

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3 }}>
      <Typography variant="h1" component="h1" gutterBottom>
        Moi
      </Typography>
      <Stack spacing={3} sx={{ mt: 2 }}>
        <Stack spacing={2}>
          <TextField
            label="Mon prénom"
            value={prenom}
            onChange={(e) => setPrenom(e.target.value)}
            onBlur={() => void enregistrer()}
            inputProps={{ maxLength: 60 }}
          />
          <TextField label="Mon adresse" value={moi.utilisateur.email} disabled />
          {!moi.utilisateur.courrielVerifieLe && (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body2" color="text.secondary">
                Cette adresse n’est pas encore confirmée : un courriel t’attend. Ça sert à retrouver
                ton mot de passe, et avant de t’abonner.
              </Typography>
              <Button
                size="small"
                onClick={() =>
                  void api.auth
                    .renvoyerVerification()
                    .then(() =>
                      dire('Le courriel vient de partir. Regarde aussi les indésirables.'),
                    )
                    .catch(direErreur)
                }
              >
                Renvoyer le courriel
              </Button>
            </Stack>
          )}
          <TextField
            label="Le nom de mon foyer"
            value={nomEspace}
            onChange={(e) => setNomEspace(e.target.value)}
            onBlur={() => void enregistrer()}
            inputProps={{ maxLength: 60 }}
          />
        </Stack>

        <Divider />

        <SectionAbonnement quotas={moi.quotas} />

        <Divider />

        <Box>
          <Typography variant="h2" component="h2" gutterBottom>
            Ma semaine
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            Un regard sur ton organisation, quand tu le demandes : une journée trop pleine, une
            tâche qui glisse, ce qui revient pour rien. Des faits, et un geste pour chacun.
            {moi.droits.includes('coaching') ? '' : ' Avec la formule Complet.'}
          </Typography>
          <Button component={LienRouteur} to="/moi/ma-semaine" variant="outlined">
            Regarder ma semaine
          </Button>
        </Box>

        <Divider />

        <SectionFoyer />

        <Divider />

        <SectionNotifications />

        <Divider />

        <SectionCalendrier />

        <Divider />

        <Box>
          <Typography variant="h2" component="h2" gutterBottom>
            Sur mon téléphone
          </Typography>
          <Typography color="text.secondary">
            Pour avoir l’app sous la main et recevoir les rappels : sur iPhone, ouvre le menu de
            partage de Safari puis « Sur l’écran d’accueil ». Sur Android, le menu du navigateur
            puis « Installer l’application ».
          </Typography>
        </Box>

        <Divider />

        <SectionCompte />

        <Divider />

        <Stack spacing={1.5}>
          {moi.utilisateur.admin && (
            <Button component={LienRouteur} to="/admin" variant="outlined">
              Administration
            </Button>
          )}
          <Button variant="outlined" onClick={() => setRetourOuvert(true)}>
            Un souci, une idée ?
          </Button>
          <Button color="inherit" onClick={() => void deconnecter().catch(direErreur)}>
            Me déconnecter
          </Button>
        </Stack>
      </Stack>
      <FormulaireRetour
        ouvert={retourOuvert}
        onFermer={() => setRetourOuvert(false)}
        ecran="/moi"
      />
    </Box>
  );
}
